interface RssFeed {
  provider: string;
  name: string;
  url: string;
  active: boolean;
  error_count: number;
  categories: string[];
  // Informational metadata documenting editorial provenance. Curated sources
  // go through the full pipeline (fetchArticleHtml, body extraction, OpenAI
  // analyzeSentiment) like all other providers. (ref: DL-004)
  curated?: boolean;
}

// Bilingual phrase lists used by the pre-fetch article filter.
interface TopicBlocklist {
  en: string[];
  de: string[];
}

interface Config {
  titles_to_ignore: string[];
  urls_to_ignore: string[];
  // Runtime topic filters travel through loadConfig() -> ConfigProcessor.
  topic_blocklist: TopicBlocklist;
  rss_feeds: RssFeed[];
  openai_integration: {
    model: string;
    max_attempts: number;
  };
  max_errors: number;
  polling: {
    interval_seconds: number;
    max_articles_per_run: number;
  };
}

interface ConfigProcessorOptions {
  rssFeedOverrides?: RssFeed[];
  skipErrorThresholdDeactivation?: boolean;
}

// Lifecycle states persisted for article body extraction writes. (DL-004)
type ContentExtractionStatus = 'success' | 'empty' | 'failed' | 'skipped';

// Additive extraction payload; summary remains the required feed contract. (DL-002)
interface ArticleContentExtraction {
  contentBody: string | null;
  contentExtractionStatus: ContentExtractionStatus;
  contentExtractionMethod: string | null;
  contentExtractionError: string | null;
  contentExtractionAttemptedAt: Date | null;
  contentExtractedAt: Date | null;
}

interface Article {
  title: string;
  link: string;
  summary: string;
  sentiment: number;
  germanCategories: string[];
  englishCategories: string[];
  feedName: string;
  provider: string;
  imageUrl: string | null;
  publishedAt: Date;
  // [DL-002] Summary remains required feed content during rollout.
  // [DL-003] Body extraction metadata is optional and additive.
  contentBody?: string | null;
  contentExtractionStatus?: ContentExtractionStatus;
  contentExtractionMethod?: string | null;
  contentExtractionError?: string | null;
  contentExtractionAttemptedAt?: Date | null;
  contentExtractedAt?: Date | null;
}

export type {
  RssFeed,
  TopicBlocklist,
  Config,
  ConfigProcessorOptions,
  Article,
  ContentExtractionStatus,
  ArticleContentExtraction,
};

export const ENGLISH_AVAILABLE_CATEGORIES = [
  'Politics',
  'Economy',
  'Society',
  'Technology',
  'Science',
  'Environment',
  'Sports',
  'Culture',
  'Global',
  'Health',
] as const;

export const GERMAN_AVAILABLE_CATEGORIES = [
  'Politik',
  'Wirtschaft',
  'Gesellschaft',
  'Technologie',
  'Wissenschaft',
  'Umwelt',
  'Sport',
  'Kultur',
  'Global',
  'Gesundheit',
] as const;

export type EnglishNewsCategory = (typeof ENGLISH_AVAILABLE_CATEGORIES)[number];
export type GermanNewsCategory = (typeof GERMAN_AVAILABLE_CATEGORIES)[number];

// EN and DE arrays must maintain identical length and 1:1 positional ordering;
// adding/removing a category requires updating both arrays in lockstep.
if (
  ENGLISH_AVAILABLE_CATEGORIES.length !== GERMAN_AVAILABLE_CATEGORIES.length
) {
  throw new Error(
    `Category array length mismatch: EN=${ENGLISH_AVAILABLE_CATEGORIES.length}, DE=${GERMAN_AVAILABLE_CATEGORIES.length}`,
  );
}

/**
 * Maps each English canonical category name to its German display equivalent.
 * Derives from the same positional arrays that define the category vocabulary,
 * keeping all category domain knowledge in one file. (ref: DL-004, INV-002)
 */
export const CATEGORY_MAP: Record<string, string> = Object.fromEntries(
  ENGLISH_AVAILABLE_CATEGORIES.map((en, i) => [
    en,
    GERMAN_AVAILABLE_CATEGORIES[i],
  ]),
);

/**
 * Returns the display name for an English canonical category in the given locale.
 * Falls back to the English form when no DE mapping exists or locale is not 'de'.
 * (ref: DL-007)
 */
export function getCategoryDisplayName(
  englishCategory: string,
  locale: string,
): string {
  if (locale === 'de' && CATEGORY_MAP[englishCategory]) {
    return CATEGORY_MAP[englishCategory];
  }
  return englishCategory;
}
